/**
 * Native App Bridge Utilities
 * 
 * This module provides a bridge between the web app and the native
 * mobile app wrapper. It detects when running inside the native app
 * and provides access to native features like image picking.
 */

// Check if we're running inside the native app
export const isNativeApp = () => {
  return typeof window !== 'undefined' && window.isNativeApp === true;
};

// Get the native platform (ios/android)
export const getNativePlatform = () => {
  if (isNativeApp()) {
    return window.nativePlatform || null;
  }
  return null;
};

/**
 * Pick an image using the native image picker
 * @param {Object} options - Options for the image picker
 * @param {boolean} options.allowsEditing - Allow image cropping
 * @param {number[]} options.aspect - Aspect ratio for cropping [width, height]
 * @param {number} options.quality - Image quality 0-1
 * @returns {Promise<Object>} - The picked image data
 */
export const pickImage = (options = {}) => {
  return new Promise((resolve, reject) => {
    if (!isNativeApp()) {
      // Fall back to file input for web
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      
      // Track if a file was selected
      let fileSelected = false;
      
      input.onchange = async (e) => {
        fileSelected = true;
        const file = e.target.files[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = (event) => {
            resolve({
              uri: URL.createObjectURL(file),
              base64: event.target.result.split(',')[1],
              mimeType: file.type,
              fileName: file.name,
            });
          };
          reader.onerror = () => reject(new Error('Failed to read file'));
          reader.readAsDataURL(file);
        } else {
          // No file selected
          resolve(null);
        }
      };
      
      // Handle cancel - when focus returns to window without file selection
      const handleFocus = () => {
        // Small delay to allow onchange to fire first
        setTimeout(() => {
          if (!fileSelected) {
            resolve(null);
          }
          window.removeEventListener('focus', handleFocus);
        }, 300);
      };
      
      window.addEventListener('focus', handleFocus);
      input.click();
      return;
    }

    // Use native image picker
    const handleMessage = (event) => {
      const message = event.detail;
      
      if (message.type === 'IMAGE_PICKED') {
        window.removeEventListener('nativeMessage', handleMessage);
        resolve(message.image);
      } else if (message.type === 'IMAGE_PICK_CANCELLED') {
        window.removeEventListener('nativeMessage', handleMessage);
        resolve(null);
      } else if (message.type === 'IMAGE_PICK_ERROR') {
        window.removeEventListener('nativeMessage', handleMessage);
        reject(new Error(message.error || 'Failed to pick image'));
      }
    };

    window.addEventListener('nativeMessage', handleMessage);
    
    // Timeout after 60 seconds
    setTimeout(() => {
      window.removeEventListener('nativeMessage', handleMessage);
      reject(new Error('Image picker timeout'));
    }, 60000);

    // Call native image picker
    window.pickImage(options);
  });
};

/**
 * Get the push notification token from the native app
 * @returns {Promise<string|null>} - The push token or null
 */
export const getPushToken = () => {
  return new Promise((resolve) => {
    if (!isNativeApp()) {
      resolve(null);
      return;
    }

    const handleMessage = (event) => {
      const message = event.detail;
      if (message.type === 'PUSH_TOKEN') {
        window.removeEventListener('nativeMessage', handleMessage);
        resolve(message.token);
      }
    };

    window.addEventListener('nativeMessage', handleMessage);
    
    setTimeout(() => {
      window.removeEventListener('nativeMessage', handleMessage);
      resolve(null);
    }, 5000);

    window.getPushToken();
  });
};

/**
 * Request a native permission
 * @param {string} permission - The permission to request (camera, media_library, notifications)
 * @returns {Promise<boolean>} - Whether the permission was granted
 */
export const requestNativePermission = (permission) => {
  return new Promise((resolve) => {
    if (!isNativeApp()) {
      resolve(true); // Assume granted on web
      return;
    }

    const handleMessage = (event) => {
      const message = event.detail;
      if (message.type === 'PERMISSION_RESULT' && message.permission === permission) {
        window.removeEventListener('nativeMessage', handleMessage);
        resolve(message.granted);
      }
    };

    window.addEventListener('nativeMessage', handleMessage);
    
    setTimeout(() => {
      window.removeEventListener('nativeMessage', handleMessage);
      resolve(false);
    }, 30000);

    window.requestNativePermission(permission);
  });
};
